import React, { useState, useEffect, useCallback, useMemo } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import PageContainer from "../layouts/PageContainer";
import CardGridContainer from "../layouts/CardGridContainer";
import PageToolbar from "../components/common/PageToolbar";
import BranchSelectFilter from "../components/common/BranchSelectFilter";
import AppButton from "../components/buttons/AppButton";
import ConfirmDialog from "../components/common/ConfirmDialog";

import ClassroomCard from "../components/classrooms/ClassroomCard";
import CreateClassroomModal from "../components/classrooms/CreateClassroomModal";
import UpdateClassroomModal from "../components/classrooms/UpdateClassroomModal";

import { usePermissions } from "../hooks/usePermissions";
import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  getClassrooms,
  deleteClassroom,
  resetClassroomServerStatus,
} from "../redux/actions/classroomActions";
import { ClassroomResponseDto } from "../shared/dtos/classroom/ClassroomResponseDto";

const ClassroomsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { hasPermission } = usePermissions();

  // --- REDUX SELECTORS ---
  const {
    items: classrooms,
    loadingFetchAll: loadingClassrooms,
    errorFetchAll: errorLoadingClassrooms,
    serverStatus,
    loadingCreate: loadingClassroomCreate,
    errorCreate: errorCreatingClassroom,
    loadingUpdate: loadingClassroomUpdate,
    errorUpdate: errorUpdatingClassroom,
    loadingDelete: loadingClassroomDelete,
  } = useAppSelector((state) => state.classroom);

  // --- LOCAL FILTER STATE ---
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedBranchId, setSelectedBranchId] = useState<string>("");

  // --- MODAL STATE ---
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedClassroom, setSelectedClassroom] =
    useState<ClassroomResponseDto | null>(null);

  // --- PERMISSIONS ---
  const canCreate = hasPermission("classroom:create");
  const canUpdate = hasPermission("classroom:update");
  const canDelete = hasPermission("classroom:delete");

  // --- FETCH DATA (Passes branchId param if super admin selects one) ---
  const fetchClassrooms = useCallback((): void => {
    dispatch(
      getClassrooms(
        selectedBranchId ? { branchId: selectedBranchId } : undefined,
      ),
    );
  }, [dispatch, selectedBranchId]);

  useEffect(() => {
    fetchClassrooms();
  }, [fetchClassrooms]);

  // --- FRONTEND FILTERING ---
  const filteredClassrooms = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return classrooms.filter((classroom) => {
      // Branch check (client-side safety alongside server query)
      const matchesBranch = selectedBranchId
        ? classroom.branchId === selectedBranchId
        : true;

      if (!matchesBranch) return false;
      if (!query) return true;

      // Search match across common classroom fields
      const matchesName = classroom.name?.toLowerCase().includes(query);
      const matchesCode = (classroom as any).code
        ?.toLowerCase()
        .includes(query);
      const matchesBuilding = (classroom as any).building
        ?.toLowerCase()
        .includes(query);

      return Boolean(matchesName || matchesCode || matchesBuilding);
    });
  }, [classrooms, searchTerm, selectedBranchId]);

  // --- MODAL HANDLERS ---
  const handleOpenCreate = (): void => setIsCreateModalOpen(true);

  const handleCloseCreate = useCallback((): void => {
    if (!loadingClassroomCreate) setIsCreateModalOpen(false);
  }, [loadingClassroomCreate]);

  const handleOpenUpdate = (classroom: ClassroomResponseDto): void => {
    setSelectedClassroom(classroom);
    setIsUpdateModalOpen(true);
  };

  const handleCloseUpdate = useCallback((): void => {
    if (!loadingClassroomUpdate) {
      setIsUpdateModalOpen(false);
      setSelectedClassroom(null);
    }
  }, [loadingClassroomUpdate]);

  const handleOpenDelete = (classroom: ClassroomResponseDto): void => {
    setSelectedClassroom(classroom);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDelete = useCallback((): void => {
    if (!loadingClassroomDelete) {
      setIsDeleteModalOpen(false);
      setSelectedClassroom(null);
    }
  }, [loadingClassroomDelete]);

  const handleDeleteConfirm = (): void => {
    if (selectedClassroom) {
      dispatch(deleteClassroom(selectedClassroom.id));
    }
  };

  // --- SERVER STATUS HANDLING ---
  useEffect(() => {
    if (!serverStatus) return;

    const { operation, status } = serverStatus;

    if (operation === "create" && status === 201) {
      handleCloseCreate();
    }
    if (operation === "update" && status === 200) {
      handleCloseUpdate();
    }
    if (operation === "delete" && status === 200) {
      handleCloseDelete();
    }

    dispatch(resetClassroomServerStatus());
  }, [
    serverStatus,
    dispatch,
    handleCloseCreate,
    handleCloseUpdate,
    handleCloseDelete,
  ]);

  return (
    <PageContainer
      title="Classrooms"
      subtitle="Manage classroom allocations, capacities, and locations."
      actions={
        canCreate && (
          <AppButton
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={handleOpenCreate}
          >
            Add Classroom
          </AppButton>
        )
      }
    >
      <PageToolbar
        searchTerm={searchTerm}
        onSearchChange={(e) => setSearchTerm(e.target.value)}
        searchPlaceholder="Search classrooms by name or code..."
      >
        {/* Only rendered if user is SUPER_ADMIN */}
        <BranchSelectFilter
          value={selectedBranchId}
          onChange={(bId) => setSelectedBranchId(bId)}
        />
      </PageToolbar>

      <CardGridContainer
        loading={loadingClassrooms}
        error={errorLoadingClassrooms}
        itemCount={filteredClassrooms.length}
        emptyMessage="No classrooms match your search criteria"
        onRetry={fetchClassrooms}
      >
        {filteredClassrooms.map((classroom) => (
          <ClassroomCard
            key={classroom.id}
            classroom={classroom}
            onEdit={canUpdate ? () => handleOpenUpdate(classroom) : undefined}
            onDelete={canDelete ? () => handleOpenDelete(classroom) : undefined}
          />
        ))}
      </CardGridContainer>

      {/* CREATE MODAL */}
      {isCreateModalOpen && (
        <CreateClassroomModal
          open={isCreateModalOpen}
          onClose={handleCloseCreate}
          loading={loadingClassroomCreate}
          error={errorCreatingClassroom}
        />
      )}

      {/* UPDATE MODAL */}
      {isUpdateModalOpen && selectedClassroom && (
        <UpdateClassroomModal
          open={isUpdateModalOpen}
          onClose={handleCloseUpdate}
          classroom={selectedClassroom}
          loading={loadingClassroomUpdate}
          error={errorUpdatingClassroom}
        />
      )}

      {/* DELETE CONFIRM DIALOG */}
      {isDeleteModalOpen && selectedClassroom && (
        <ConfirmDialog
          open={isDeleteModalOpen}
          onClose={handleCloseDelete}
          onConfirm={handleDeleteConfirm}
          title="Delete Classroom"
          description={`Are you sure you want to delete "${selectedClassroom.name}"? This action cannot be undone.`}
          loading={loadingClassroomDelete}
        />
      )}
    </PageContainer>
  );
};

export default ClassroomsPage;
